import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LineChart, BarChart2, TrendingUp, Calendar, Settings, Search, Copy, RefreshCcw, AlertCircle, Wifi, BarChart, Twitter, Globe, MessageCircle, Youtube, X, DollarSign, TrendingDown } from 'lucide-react';
import { Token } from '../../types/supabase';
import { getTokenChartData, OHLCVData } from '../../services/solanatrackerService';
import TokenSearchModal from '../../components/TokenSearchModal';
import customDatafeed from './datafeed.ts';
import wsService from '../../services/websocketService';
import tokenMarketDataService, { ProcessedTokenMarketData } from '../../services/tokenMarketDataService';
import { tradeLoggingService } from '../../services/tradeLoggingService';
import { tradeEngine } from '../../services/tradeEngine';

// Define the widget constructor which is loaded from the window
declare global {
  interface Window {
    TradingView: {
      widget: any;
    };
  }
}

// Interface for the widget options
interface ChartingLibraryWidgetOptions {
  symbol: string;
  interval?: string;
  container: string;
  datafeed: any;
  library_path: string;
  locale?: string;
  disabled_features?: string[];
  enabled_features?: string[];
  theme?: string;
  autosize?: boolean;
  overrides?: any;
  time_frames?: any[];
  custom_css_url?: string;
  charts_storage_url?: string;
  charts_storage_api_version?: string;
  client_id?: string;
  user_id?: string;
  fullscreen?: boolean;
  loading_screen?: any;
  studies_overrides?: any;
  datafeed_management?: any;
}

// Interface for the widget instance
interface IChartingLibraryWidget {
  onChartReady(callback: () => void): void;
  remove(): void;
  resize(width: number, height: number): void;
}

interface AdvancedChartsProps {
  selectedToken?: Token;
  onClose?: () => void;
  isWidget?: boolean;
}

// Interface for position tracking
interface UserPosition {
  tokenAddress: string;
  tokenSymbol: string;
  tokenName: string;
  totalBought: number; // Total tokens bought
  totalSold: number; // Total tokens sold
  currentPosition: number; // Net position (bought - sold)
  averageEntryPrice: number; // Average price paid
  totalSolInvested: number; // Total SOL spent
  totalSolReceived: number; // Total SOL received from sells
  netSolPosition: number; // Net SOL position
  trades: UserTrade[];
}

interface UserTrade {
  id: string;
  transactionSignature: string;
  tradeType: 'buy' | 'sell';
  tokenAmount: number;
  solAmount: number;
  pricePerToken: number;
  timestamp: string;
  status: 'pending' | 'confirmed' | 'failed';
}

const AdvancedCharts: React.FC<AdvancedChartsProps> = ({ selectedToken, onClose, isWidget = false }) => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const [chartInstance, setChartInstance] = useState<IChartingLibraryWidget | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMarketData, setIsLoadingMarketData] = useState<boolean>(false);
  const [chartHeight, setChartHeight] = useState<number>(400);
  const [chartData, setChartData] = useState<OHLCVData[]>([]);
  const [scriptsLoaded, setScriptsLoaded] = useState<boolean>(false);
  const [currentTimeframe, setCurrentTimeframe] = useState<string>('1m');
  const [copied, setCopied] = useState(false);
  const [hasInitialData, setHasInitialData] = useState<boolean>(false);
  
  // Token search state
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [localSelectedToken, setLocalSelectedToken] = useState<Token | undefined>(selectedToken);
  
  // WebSocket connection status
  const [wsConnected, setWsConnected] = useState<boolean>(true);
  
  // Token market data from token room
  const [tokenMarketData, setTokenMarketData] = useState<ProcessedTokenMarketData | null>(null);
  
  // Position tracking state
  const [userPosition, setUserPosition] = useState<UserPosition | null>(null);
  const [isLoadingPosition, setIsLoadingPosition] = useState(false);
  const [showClosePositionModal, setShowClosePositionModal] = useState(false);
  const [closePositionPercentage, setClosePositionPercentage] = useState(100);
  const [isClosingPosition, setIsClosingPosition] = useState(false);
  
  // Chart position lines tracking
  const positionLinesRef = useRef<Map<string, any>>(new Map()); // Store TradingView line references
  
  // Add debugging state
  const [debugInfo, setDebugInfo] = useState({
    subscriptionActive: false,
    lastUpdateTime: null as Date | null,
    messageCount: 0,
    connectionStatus: 'unknown',
    currentRoom: ''
  });
  
  // Use the local token state if available, otherwise use the prop
  const effectiveToken = localSelectedToken || selectedToken;
  
  // Function to format social URLs properly
  const formatSocialUrl = (url: string | null | undefined, platform: 'twitter' | 'website'): string | null => {
    if (!url) return null;
    
    // If it's already a full URL, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If it's a Twitter handle or username, format it properly
    if (platform === 'twitter') {
      // Remove @ symbol if present
      const handle = url.replace('@', '');
      return `https://x.com/${handle}`;
    }
    
    // For websites, add https if missing
    if (platform === 'website') {
      return `https://${url}`;
    }
    
    return url;
  };
  
  // Format compact numbers with k/m/b suffixes
  const formatCompactNumber = useCallback((num: number) => {
    if (num >= 1_000_000_000) {
      return `$${(num / 1_000_000_000).toFixed(2)}B`;
    } else if (num >= 1_000_000) {
      return `$${(num / 1_000_000).toFixed(2)}M`;
    } else if (num >= 1_000) {
      return `$${(num / 1_000).toFixed(2)}k`;
    } else if (num < 0.00001) {
      // Format very small numbers without scientific notation
      return `$${num.toFixed(8)}`;
    } else if (num < 0.001) {
      return `$${num.toFixed(6)}`;
    } else if (num < 1) {
      return `$${num.toFixed(4)}`;
    } else {
      return `$${num.toFixed(2)}`;
    }
  }, []);
  
  // Function to broadcast token selection to other components
  const broadcastTokenSelection = useCallback((token: Token) => {
    console.log(`AdvancedCharts: Broadcasting token selection: ${token.symbol}`);
    const event = new CustomEvent('ekko:token_selected', { 
      detail: { token },
      bubbles: true
    });
    window.dispatchEvent(event);
  }, []);
  
  // Price stats calculated from chart data
  const [priceStats, setPriceStats] = useState({
    current: 0,
    change: 0,
    high24h: 0,
    low24h: 0,
    volume24h: 0,
  });
  
  // Fetch token market data using polling
  const fetchTokenMarketData = useCallback(async (tokenMint: string) => {
    console.log(`📡 AdvancedCharts: Fetching market data for ${tokenMint}`);
    setIsLoadingMarketData(true);
    try {
      const marketData = await tokenMarketDataService.fetchTokenMarketData(tokenMint);
      if (marketData) {
        console.log(`✅ AdvancedCharts: Received market data for ${tokenMint}:`, marketData);
        setTokenMarketData(marketData);
        
        // Update price stats with real-time data if available
        if (marketData.priceChange['24h'] !== undefined) {
          setPriceStats(prev => ({
            ...prev,
            current: marketData.price.usd,
            change: marketData.priceChange['24h']
          }));
        }
      } else {
        console.warn(`⚠️ AdvancedCharts: No market data received for ${tokenMint}`);
      }
    } catch (error) {
      console.error(`❌ AdvancedCharts: Error fetching market data for ${tokenMint}:`, error);
    } finally {
      setIsLoadingMarketData(false);
    }
  }, []);

  // Function to fetch user position for current token
  const fetchUserPosition = useCallback(async (tokenAddress: string) => {
    console.log(`📊 [DEBUG] Starting fetchUserPosition for token: ${tokenAddress}`);
    
    // Get wallet address - try multiple methods
    const walletAddress = (window as any).solana?.publicKey?.toString() || 
                         (window as any).solflare?.publicKey?.toString() ||
                         (window as any).phantom?.solana?.publicKey?.toString();
    
    console.log(`📊 [DEBUG] Wallet detection:`, {
      solana: !!(window as any).solana?.publicKey,
      solflare: !!(window as any).solflare?.publicKey,
      phantom: !!(window as any).phantom?.solana?.publicKey,
      walletAddress: walletAddress
    });
    
    if (!walletAddress) {
      console.log('📊 No wallet connected, skipping position fetch');
      setUserPosition(null);
      return;
    }

    setIsLoadingPosition(true);
    try {
      console.log(`📊 Fetching user position for token: ${tokenAddress}, wallet: ${walletAddress}`);
      
      // First, let's try to get ALL trades for this user to see what's in the database
      console.log(`📊 [DEBUG] Fetching ALL trades for wallet: ${walletAddress}`);
      const { data: allTrades, error: allTradesError } = await tradeLoggingService.getUserTrades(walletAddress, {
        limit: 1000 // Get all trades
      });
      
      console.log(`📊 [DEBUG] All trades result:`, { 
        tradesCount: allTrades?.length || 0, 
        error: allTradesError,
        trades: allTrades?.slice(0, 3) // Show first 3 trades for debugging
      });
      
      // Now fetch trades for this specific token
      const { data: trades, error } = await tradeLoggingService.getUserTrades(walletAddress, {
        tokenAddress: tokenAddress,
        status: 'confirmed', // Only confirmed trades
        limit: 1000 // Get all trades for this token
      });

      console.log(`📊 [DEBUG] Token-specific trades result:`, { 
        tokenAddress,
        tradesCount: trades?.length || 0, 
        error: error,
        trades: trades?.slice(0, 3) // Show first 3 trades for debugging
      });

      if (error) {
        console.error('❌ Error fetching user trades:', error);
        setUserPosition(null);
        return;
      }

      if (!trades || trades.length === 0) {
        console.log('📊 No trades found for this token');
        setUserPosition(null);
        return;
      }

      console.log(`📊 Found ${trades.length} trades for token ${tokenAddress}`);

      // Calculate position from trades
      let totalBought = 0;
      let totalSold = 0;
      let totalSolInvested = 0;
      let totalSolReceived = 0;
      let weightedPriceSum = 0; // Sum of (price * quantity) for weighted average
      let totalBoughtAmount = 0;

      const userTrades: UserTrade[] = trades.map(trade => {
        console.log(`📊 [DEBUG] Processing trade:`, {
          signature: trade.transaction_signature,
          type: trade.trade_type,
          input_amount: trade.input_amount,
          output_amount: trade.output_amount,
          sol_amount: trade.sol_amount,
          token_price_usd: trade.token_price_usd,
          status: trade.status
        });
        
        // For buy trades: input_amount is SOL, output_amount is tokens
        // For sell trades: input_amount is tokens, output_amount is SOL
        let tokenAmount = 0;
        let solAmount = 0;
        
        if (trade.trade_type === 'buy') {
          // Buy: We spent SOL (input) to get tokens (output)
          tokenAmount = parseFloat(trade.output_amount || '0');
          solAmount = parseFloat(trade.input_amount || '0');
        } else if (trade.trade_type === 'sell') {
          // Sell: We spent tokens (input) to get SOL (output)
          tokenAmount = parseFloat(trade.input_amount || '0');
          solAmount = parseFloat(trade.output_amount || '0');
        }
        
        // Use token_price_usd if available for more accurate price calculation
        let pricePerToken = 0;
        if (trade.token_price_usd && parseFloat(trade.token_price_usd) > 0) {
          pricePerToken = parseFloat(trade.token_price_usd);
        } else if (tokenAmount > 0 && solAmount > 0) {
          // Fallback: calculate from SOL amount (need to convert SOL to USD)
          // This is less accurate but better than nothing
          pricePerToken = solAmount / tokenAmount; // This gives SOL per token, not USD per token
        }

        if (trade.trade_type === 'buy') {
          totalBought += tokenAmount;
          totalSolInvested += solAmount;
          // For weighted average price calculation, use USD price if available
          if (pricePerToken > 0) {
            weightedPriceSum += (pricePerToken * tokenAmount); // price * quantity
          }
          totalBoughtAmount += tokenAmount;
          console.log(`📊 [DEBUG] Buy trade: +${tokenAmount} tokens, +${solAmount} SOL invested, price: $${pricePerToken}`);
        } else if (trade.trade_type === 'sell') {
          totalSold += tokenAmount;
          totalSolReceived += solAmount;
          console.log(`📊 [DEBUG] Sell trade: -${tokenAmount} tokens, +${solAmount} SOL received, price: $${pricePerToken}`);
        }

        return {
          id: trade.id || trade.transaction_signature,
          transactionSignature: trade.transaction_signature,
          tradeType: trade.trade_type as 'buy' | 'sell',
          tokenAmount,
          solAmount,
          pricePerToken,
          timestamp: trade.created_at,
          status: trade.status as 'pending' | 'confirmed' | 'failed'
        };
      });

      const currentPosition = totalBought - totalSold;
      // Calculate weighted average entry price in USD
      const averageEntryPrice = totalBoughtAmount > 0 && weightedPriceSum > 0 
        ? weightedPriceSum / totalBoughtAmount 
        : 0;
      const netSolPosition = totalSolReceived - totalSolInvested;

              console.log(`📊 [DEBUG] Position calculation:`, {
          totalBought,
          totalSold,
          currentPosition,
          averageEntryPrice,
          weightedPriceSum,
          totalBoughtAmount,
          totalSolInvested,
          totalSolReceived,
          netSolPosition
        });

      if (currentPosition > 0) {
        const position: UserPosition = {
          tokenAddress,
          tokenSymbol: effectiveToken?.symbol || 'Unknown',
          tokenName: effectiveToken?.name || 'Unknown',
          totalBought,
          totalSold,
          currentPosition,
          averageEntryPrice,
          totalSolInvested,
          totalSolReceived,
          netSolPosition,
          trades: userTrades
        };

        console.log(`📊 ✅ Calculated position:`, position);
        setUserPosition(position);
        
        // Add position lines to chart if chart is ready
        if (chartInstance) {
          setTimeout(() => {
            addPositionLinesToChart(position);
          }, 1000); // Small delay to ensure chart is fully loaded
        }
      } else {
        console.log('📊 No open position for this token (currentPosition <= 0)');
        setUserPosition(null);
        
        // Clear any existing position lines
        clearPositionLines();
      }

    } catch (error) {
      console.error('❌ Error calculating user position:', error);
      setUserPosition(null);
    } finally {
      setIsLoadingPosition(false);
    }
  }, [effectiveToken]);

  // Function to close position
  const handleClosePosition = useCallback(async () => {
    if (!userPosition || !effectiveToken) return;

    const walletAddress = window.solana?.publicKey?.toString() || 
                         window.solflare?.publicKey?.toString();
    
    if (!walletAddress) {
      console.error('❌ No wallet connected');
      return;
    }

    setIsClosingPosition(true);
    try {
      // Calculate amount to sell based on percentage
      const amountToSell = (userPosition.currentPosition * closePositionPercentage) / 100;
      
      console.log(`📊 Closing ${closePositionPercentage}% of position: ${amountToSell} tokens`);

      // Execute sell trade using trade engine
      const result = await tradeEngine.executeSwap({
        inputMint: effectiveToken.mint, // Token mint
        outputMint: 'So11111111111111111111111111111111111111112', // SOL mint
        amount: amountToSell.toString(),
        userWalletAddress: walletAddress,
        tradingWalletAddress: walletAddress, // Use same wallet for manual trades
        slippagePercent: '20' // Default 20% slippage for position closing
      });

      if (result.status === 'CONFIRMED') {
        console.log(`✅ Position closed successfully: ${result.signature}`);
        
        // Refresh position after successful trade
        setTimeout(() => {
          fetchUserPosition(effectiveToken.mint);
        }, 2000);
        
        setShowClosePositionModal(false);
        setClosePositionPercentage(100);
      } else {
        console.error(`❌ Failed to close position: Status ${result.status}`);
      }

    } catch (error) {
      console.error('❌ Error closing position:', error);
    } finally {
      setIsClosingPosition(false);
    }
  }, [userPosition, effectiveToken, closePositionPercentage, fetchUserPosition]);

  // Function to add position lines to the chart
  const addPositionLinesToChart = useCallback((position: UserPosition) => {
    if (!chartInstance || !effectiveToken) return;

    // Validate position data before drawing lines
    if (!position.averageEntryPrice || position.averageEntryPrice <= 0) {
      console.warn(`📊 Invalid entry price for position lines: ${position.averageEntryPrice}`);
      return;
    }

    try {
      console.log(`📊 Adding position lines to chart for ${effectiveToken.symbol}, entry price: $${position.averageEntryPrice.toFixed(8)}`);
      
      // Clear existing position lines
      clearPositionLines();

      // Get the chart widget
      const chart = (chartInstance as any).activeChart?.() || (chartInstance as any).chart?.();
      if (!chart) {
        console.warn('⚠️ Chart not available for adding position lines');
        return;
      }

      // Add entry line using createOrderLine (more professional looking)
      try {
        const entryLine = chart.createOrderLine();
        if (entryLine) {
          entryLine.setPrice(position.averageEntryPrice);
          entryLine.setText(`Entry: $${position.averageEntryPrice.toFixed(6)}`);
          entryLine.setLineColor('#24c35b'); // Green for entry
          entryLine.setBodyText('ENTRY');
          entryLine.setLineStyle(0); // Solid line
          entryLine.setLineLength(25);
          entryLine.setQuantity(position.currentPosition.toFixed(2));
          
          // Store reference for cleanup
          positionLinesRef.current.set('entry', entryLine);
          console.log(`✅ Added entry line at $${position.averageEntryPrice.toFixed(6)}`);
        }
      } catch (orderLineError) {
        console.warn('⚠️ createOrderLine failed, trying createShape fallback:', orderLineError);
        
        // Fallback to createShape if createOrderLine fails
        try {
          const entryShape = chart.createShape(
            {
              time: Math.floor(Date.now() / 1000), // Current time
              price: position.averageEntryPrice,
            },
            {
              shape: 'horizontal_line',
              text: `Entry: $${position.averageEntryPrice.toFixed(6)}`,
              color: '#24c35b',
              disableSelection: false,
              disableSave: true,
              linewidth: 2,
              linestyle: 0, // Solid
            }
          );
          
          if (entryShape) {
            positionLinesRef.current.set('entry_shape', entryShape);
            console.log(`✅ Added entry shape at $${position.averageEntryPrice.toFixed(6)}`);
          }
        } catch (shapeError) {
          console.error('❌ Failed to create entry shape:', shapeError);
        }
      }

      // Add current price line for reference (if we have current market data)
      if (tokenMarketData?.price?.usd) {
        try {
          const currentPriceLine = chart.createOrderLine();
          if (currentPriceLine) {
            currentPriceLine.setPrice(tokenMarketData.price.usd);
            currentPriceLine.setText(`Current: $${tokenMarketData.price.usd.toFixed(6)}`);
            currentPriceLine.setLineColor('#fbbf24'); // Yellow for current price
            currentPriceLine.setBodyText('CURRENT');
            currentPriceLine.setLineStyle(1); // Dashed line
            currentPriceLine.setLineLength(20);
            
            positionLinesRef.current.set('current', currentPriceLine);
            console.log(`✅ Added current price line at $${tokenMarketData.price.usd.toFixed(6)}`);
          }
        } catch (currentLineError) {
          console.warn('⚠️ Failed to add current price line:', currentLineError);
        }
      }

      // Add profit/loss zones if we have enough data
      if (tokenMarketData?.price?.usd && position.averageEntryPrice > 0) {
        const currentPrice = tokenMarketData.price.usd;
        const entryPrice = position.averageEntryPrice;
        const pnlPercent = ((currentPrice - entryPrice) / entryPrice) * 100;
        
        // Add break-even line
        try {
          const breakEvenShape = chart.createShape(
            {
              time: Math.floor(Date.now() / 1000),
              price: entryPrice,
            },
            {
              shape: 'horizontal_line',
              text: `Break Even`,
              color: '#6b7280',
              disableSelection: true,
              disableSave: true,
              linewidth: 1,
              linestyle: 2, // Dotted
            }
          );
          
          if (breakEvenShape) {
            positionLinesRef.current.set('breakeven', breakEvenShape);
          }
        } catch (breakEvenError) {
          console.warn('⚠️ Failed to add break-even line:', breakEvenError);
        }

        console.log(`📊 Position P&L: ${pnlPercent.toFixed(2)}% (Entry: $${entryPrice.toFixed(6)}, Current: $${currentPrice.toFixed(6)})`);
      }

    } catch (error) {
      console.error('❌ Error adding position lines to chart:', error);
    }
  }, [chartInstance, effectiveToken, tokenMarketData]);

  // Function to clear position lines from the chart
  const clearPositionLines = useCallback(() => {
    try {
      console.log(`🧹 Clearing position lines from chart`);
      
      positionLinesRef.current.forEach((line, key) => {
        try {
          if (line && typeof line.remove === 'function') {
            line.remove();
            console.log(`✅ Removed position line: ${key}`);
          }
        } catch (removeError) {
          console.warn(`⚠️ Failed to remove position line ${key}:`, removeError);
        }
      });
      
      positionLinesRef.current.clear();
    } catch (error) {
      console.error('❌ Error clearing position lines:', error);
    }
  }, []);

  // Poll for token market data when token changes
  useEffect(() => {
    if (effectiveToken?.mint) {
      // Reset token market data when token changes
      setTokenMarketData(null);
      
      console.log(`🔄 AdvancedCharts: Starting market data polling for ${effectiveToken.mint}`);
      
      // Fetch initial data immediately
      fetchTokenMarketData(effectiveToken.mint);
      
      // Set up polling interval (every 30 seconds)
      const pollInterval = setInterval(() => {
        fetchTokenMarketData(effectiveToken.mint);
      }, 30000);
      
      // Cleanup: clear interval when token changes
      return () => {
        console.log(`🛑 AdvancedCharts: Stopping market data polling for ${effectiveToken.mint}`);
        clearInterval(pollInterval);
      };
    }
  }, [effectiveToken?.mint, fetchTokenMarketData]);

  // Fetch user position when token changes
  useEffect(() => {
    if (effectiveToken?.mint) {
      console.log(`📊 AdvancedCharts: Fetching user position for ${effectiveToken.mint}`);
      fetchUserPosition(effectiveToken.mint);
    }
  }, [effectiveToken?.mint, fetchUserPosition]);

  // Update position lines when position or market data changes
  useEffect(() => {
    if (chartInstance && userPosition) {
      console.log(`📊 Position or market data changed, updating chart lines`);
      setTimeout(() => {
        addPositionLinesToChart(userPosition);
      }, 500); // Small delay to ensure chart is ready
    } else if (chartInstance && !userPosition) {
      // Clear lines when no position
      clearPositionLines();
    }
  }, [userPosition, tokenMarketData?.price?.usd, chartInstance, addPositionLinesToChart, clearPositionLines]);

  // Update local state when prop changes
  useEffect(() => {
    if (selectedToken) {
      console.log(`🔄 AdvancedCharts: Token prop changed to ${selectedToken.symbol} (${selectedToken.mint})`);
      setLocalSelectedToken(selectedToken);
      // Reset market data when token changes
      setTokenMarketData(null);
      // Reset data state to force refetch
      setHasInitialData(false);
      setChartData([]);
      // Force chart re-initialization by temporarily clearing chart instance
      if (chartInstance) {
        try {
          console.log(`🗑️ AdvancedCharts: Removing existing chart instance for token change`);
          chartInstance.remove();
          setChartInstance(null);
        } catch (err) {
          console.warn('Error removing chart instance during token change:', err);
        }
      }
    }
  }, [selectedToken]);

  // Debug WebSocket status and chart updates
  useEffect(() => {
    console.log(`🔍 [DEBUG] Setting up debug monitoring for token: ${effectiveToken?.mint}`);
    
    const interval = setInterval(() => {
      setDebugInfo(prev => ({
        ...prev,
        connectionStatus: wsService.isConnected() ? 'connected' : 'disconnected'
      }));
    }, 2000);

    // Listen for WebSocket messages to track activity
    const handleWebSocketMessage = (data: any) => {
      console.log(`📨 [DEBUG] Received WebSocket message for ${effectiveToken?.mint}:`, data);
      setDebugInfo(prev => ({
        ...prev,
        lastUpdateTime: new Date(),
        messageCount: prev.messageCount + 1
      }));
    };

    // Add global listener for debugging if we have a token
    let unsubscribe: (() => void) | undefined;
    if (effectiveToken?.mint) {
      const room = `price:${effectiveToken.mint}`;
      console.log(`🔍 [DEBUG] Monitoring WebSocket room: ${room}`);
      
      unsubscribe = wsService.on(room, handleWebSocketMessage);
      
      setDebugInfo(prev => ({ 
        ...prev, 
        subscriptionActive: true,
        currentRoom: room,
        messageCount: 0 // Reset count for new token
      }));
    } else {
      setDebugInfo(prev => ({ 
        ...prev, 
        subscriptionActive: false,
        currentRoom: ''
      }));
    }

    return () => {
      clearInterval(interval);
      if (unsubscribe) {
        console.log(`🔍 [DEBUG] Cleaning up debug monitoring for ${effectiveToken?.mint}`);
        unsubscribe();
      }
      setDebugInfo(prev => ({ ...prev, subscriptionActive: false }));
    };
  }, [effectiveToken?.mint]);

  // Listen for WebSocket connection status (still needed for chart data)
  useEffect(() => {
    // Check initial connection status
    setWsConnected(wsService.isConnected());
    
    // Create event listeners for connection status changes
    const handleConnectionChange = (status: any) => {
      console.log('WebSocket connection status changed:', status);
      setWsConnected(wsService.isConnected());
    };
    
    // Listen for custom events from datafeed
    window.addEventListener('ekko:websocket_status', handleConnectionChange as EventListener);
    
    // Check connection status periodically
    const statusInterval = setInterval(() => {
      setWsConnected(wsService.isConnected());
    }, 5000);
    
    return () => {
      window.removeEventListener('ekko:websocket_status', handleConnectionChange as EventListener);
      clearInterval(statusInterval);
    };
  }, []);

  // Listen for token selection events from other components
  useEffect(() => {
    const handleTokenSelected = (event: CustomEvent<{ token: Token }>) => {
      const { token } = event.detail;
      console.log('AdvancedCharts: Token selection event received:', token.symbol);
      setLocalSelectedToken(token);
      // Reset market data when token changes
      setTokenMarketData(null);
      // Reset data state to force refetch
      setHasInitialData(false);
      setChartData([]);
      
      // Force chart re-initialization
      if (chartInstance) {
        try {
          chartInstance.remove();
          setChartInstance(null);
        } catch (err) {
          console.warn('Error removing chart instance during token change from event:', err);
        }
      }
    };
    
    // Add event listener
    window.addEventListener('ekko:token_selected', handleTokenSelected as EventListener);
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('ekko:token_selected', handleTokenSelected as EventListener);
    };
  }, [chartInstance]);



  // Format price with appropriate decimal places and suffixes (k/M/B)
  const formatPrice = useCallback((price: number) => {
    if (price >= 1_000_000_000) {
      return '$' + (price / 1_000_000_000).toFixed(2) + 'B';
    } else if (price >= 1_000_000) {
      return '$' + (price / 1_000_000).toFixed(2) + 'M';
    } else if (price >= 1000) {
      return '$' + (price / 1000).toFixed(2) + 'k';
    } else if (price < 0.00001) {
      // Format very small numbers without scientific notation
      return '$' + price.toFixed(8);
    } else if (price < 0.001) {
      return '$' + price.toFixed(6);
    } else if (price < 1) {
      return '$' + price.toFixed(4);
    } else {
      return '$' + price.toFixed(2);
    }
  }, []);

  // Format numbers for display
  const formatNumber = useCallback((num: number, prefix = '') => {
    if (num >= 1_000_000_000) {
      return `${prefix}${(num / 1_000_000_000).toFixed(2)}B`;
    } else if (num >= 1_000_000) {
      return `${prefix}${(num / 1_000_000).toFixed(2)}M`;
    } else if (num >= 1_000) {
      return `${prefix}${(num / 1_000).toFixed(2)}K`;
    } else {
      return `${prefix}${num.toFixed(2)}`;
    }
  }, []);

  // Format holders count (no decimals, use commas for thousands)
  const formatHolders = useCallback((num: number) => {
    if (num >= 1000) {
      return num.toLocaleString(); // This will add commas: 1,200, 10,500, etc.
    } else {
      return num.toString(); // Single digits: 1, 50, 999, etc.
    }
  }, []);

  // Format volume with appropriate units
  const formatVolume = useCallback((volume: number) => {
    return formatNumber(volume, '$');
  }, [formatNumber]);
  
  // Convert timeframe to TradingView resolution (for chart display)
  const timeframeToResolution = useCallback((timeframe: string): string => {
    switch (timeframe) {
      case '1s': return '1S';
      case '5s': return '5S';
      case '15s': return '15S';
      case '30s': return '30S';
      case '1m': return '1';
      case '5m': return '5';
      case '15m': return '15';
      case '30m': return '30';
      case '1h': return '60';
      case '2h': return '120';
      case '4h': return '240';
      case '6h': return '360';
      case '8h': return '480';
      case '12h': return '720';
      case '1d': return 'D';
      case '1w': return 'W';
      case '1M': return 'M';
      default: return '60'; // Default to 1h
    }
  }, []);

  // Load TradingView scripts
  useEffect(() => {
    const loadScripts = async () => {
      try {
        console.log('🔍 Checking TradingView library availability...');
        
        // Check if scripts are already loaded
        if (window.TradingView && window.TradingView.widget) {
          console.log('✅ TradingView library already loaded');
          setScriptsLoaded(true);
          return;
        }

        console.log('📥 TradingView library not found, attempting to load...');

        // Check if script tag already exists
        const existingScript = document.querySelector('script[src="/charting_library/charting_library.standalone.js"]');
        if (existingScript) {
          console.log('⚠️ Script tag exists but library not loaded, removing and retrying...');
          existingScript.remove();
        }

        // Load charting library script - use web path
        const chartingLibraryScript = document.createElement('script');
        chartingLibraryScript.src = '/charting_library/charting_library.standalone.js';
        chartingLibraryScript.async = true;
        chartingLibraryScript.type = 'text/javascript';
        
        console.log('📦 Adding script tag to document head...');
        document.head.appendChild(chartingLibraryScript);
        
        // Set up a timeout to detect loading failures
        const loadTimeout = setTimeout(() => {
          console.error('❌ TradingView script loading timeout (10 seconds)');
          console.log('🔍 Checking if script loaded but window.TradingView is not available...');
          
          // Check if TradingView is available after timeout
          if (window.TradingView) {
            console.log('📜 Script loaded but TradingView widget not available. Checking window object...');
            console.log('🔍 Available on window:', Object.keys(window).filter(key => key.toLowerCase().includes('trading')));
          } else {
            console.log('❌ TradingView not available on window after timeout');
          }
        }, 10000);
        
        // Wait for scripts to load
        chartingLibraryScript.onload = () => {
          clearTimeout(loadTimeout);
          console.log('✅ Script onload event fired');
          
          // Double-check that TradingView is actually available
          if (window.TradingView && window.TradingView.widget) {
            console.log('✅ TradingView library confirmed available');
            setScriptsLoaded(true);
          } else {
            console.error('❌ Script loaded but TradingView.widget not available');
            console.log('🔍 window.TradingView:', window.TradingView);
            
            // Try a delayed check in case it takes time to initialize
            setTimeout(() => {
              if (window.TradingView && window.TradingView.widget) {
                console.log('✅ TradingView library available after delay');
                setScriptsLoaded(true);
              } else {
                console.error('❌ TradingView still not available after delay');
              }
            }, 1000);
          }
        };

        chartingLibraryScript.onerror = (error) => {
          clearTimeout(loadTimeout);
          console.error('❌ Error loading charting library:', error);
          console.log('🔍 Script src:', chartingLibraryScript.src);
          
          // Try alternative loading method
          console.log('🔄 Attempting alternative loading method...');
          fetch('/charting_library/charting_library.standalone.js')
            .then(response => {
              console.log('📡 Fetch response status:', response.status);
              if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
              }
              return response.text();
            })
            .then(scriptContent => {
              console.log('📜 Script content length:', scriptContent.length);
              if (scriptContent.length < 1000) {
                console.error('❌ Script content too short, likely an error page');
                console.log('📄 Script content preview:', scriptContent.substring(0, 500));
              } else {
                console.log('✅ Script content looks valid, executing...');
                // Execute the script content
                const scriptElement = document.createElement('script');
                scriptElement.textContent = scriptContent;
                document.head.appendChild(scriptElement);
                
                // Check if TradingView is now available
                setTimeout(() => {
                  if (window.TradingView && window.TradingView.widget) {
                    console.log('✅ TradingView loaded via alternative method');
                    setScriptsLoaded(true);
                  } else {
                    console.error('❌ Alternative loading method failed');
                  }
                }, 500);
              }
            })
            .catch(fetchError => {
              console.error('❌ Fetch error:', fetchError);
            });
        };
      } catch (error) {
        console.error('❌ Error in loadScripts function:', error);
      }
    };
    
    loadScripts();
  }, []);

  // Handle window resize for responsive chart
  useEffect(() => {
    const updateDimensions = () => {
      if (chartContainerRef.current) {
        const containerElement = chartContainerRef.current;
        const { clientWidth, clientHeight } = containerElement;
        setChartHeight(clientHeight);
        
        // Resize the chart if it exists
        if (chartInstance && typeof chartInstance.resize === 'function') {
          chartInstance.resize(clientWidth, clientHeight);
        }
      }
    };

    // Do initial resize
    updateDimensions();
    
    // Also listen to window resize
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [chartInstance]);
  
  // Fetch initial chart data
  useEffect(() => {
    if (effectiveToken?.mint) {
      console.log(`🔄 AdvancedCharts: Triggering fetchChartData for token change - ${effectiveToken.symbol} (${effectiveToken.mint})`);
      fetchChartData(effectiveToken.mint, currentTimeframe);
    }
  }, [effectiveToken?.mint, currentTimeframe]);

  // Initialize the TradingView chart
  useEffect(() => {
    if (!chartContainerRef.current || !scriptsLoaded || !hasInitialData) {
      console.log(`🚫 AdvancedCharts: Chart initialization blocked:`, {
        hasContainer: !!chartContainerRef.current,
        scriptsLoaded: scriptsLoaded,
        hasInitialData: hasInitialData
      });
      return;
    }
    
    // Use a default token if none is selected for testing
    const symbolToUse = effectiveToken?.mint || 'So11111111111111111111111111111111111111112'; // Default to SOL
    
    console.log(`📊 AdvancedCharts: Initializing chart for symbol: ${symbolToUse}`);
    
    // Clean up existing chart instance
    if (chartInstance) {
      console.log('🗑️ AdvancedCharts: Removing existing chart instance');
      try {
      chartInstance.remove();
      } catch (err) {
        console.warn('Error removing existing chart instance:', err);
      }
      setChartInstance(null);
    }
    
    // Verify TradingView library is loaded
    if (!window.TradingView || !window.TradingView.widget) {
      console.error('❌ TradingView library not loaded. Checking script path...');
      
      // Try to load the script again if it failed
      const existingScript = document.querySelector('script[src="/charting_library/charting_library.standalone.js"]');
      if (!existingScript) {
        console.log('📥 Attempting to reload TradingView script...');
        const chartingLibraryScript = document.createElement('script');
        chartingLibraryScript.src = '/charting_library/charting_library.standalone.js';
        chartingLibraryScript.async = true;
        
        chartingLibraryScript.onload = () => {
          console.log('✅ TradingView script loaded successfully');
          setScriptsLoaded(true);
        };

        chartingLibraryScript.onerror = (error) => {
          console.error('❌ Error loading charting library:', error);
        };
        
        document.head.appendChild(chartingLibraryScript);
      }
      return;
    }
    
    console.log('✅ TradingView library is available');
    
    // Clear the container
    if (chartContainerRef.current.children.length > 0) {
      chartContainerRef.current.innerHTML = '';
    }
    
    // Add a preloader with white background and EKKO logo
    const preloader = document.createElement('div');
    preloader.style.position = 'absolute';
    preloader.style.inset = '0';
    preloader.style.backgroundColor = 'white';
    preloader.style.display = 'flex';
    preloader.style.alignItems = 'center';
    preloader.style.justifyContent = 'center';
    preloader.style.zIndex = '1';
    preloader.innerHTML = `
      <div style="display: flex; flex-direction: column; align-items: center; gap: 15px;">
        <img src="/src/assets/images/EkkoLogo3.png" alt="EKKO" style="height: 48px; opacity: 0.8; filter: brightness(0); object-fit: contain;" />
        <div style="width: 30px; height: 30px; border: 2px solid rgba(36, 195, 91, 0.2); border-top-color: #24c35b; border-radius: 50%; animation: ekko-spin 1s linear infinite;"></div>
        <div style="color: #000000; margin-top: 5px; font-size: 12px;">Loading Chart...</div>
        <style>
          @keyframes ekko-spin { to { transform: rotate(360deg); } }
        </style>
      </div>
    `;
    chartContainerRef.current.appendChild(preloader);
    
    // Create a unique ID for the chart container
    const containerId = `tv_chart_container_${symbolToUse}_${Date.now()}`;
    const containerElement = document.createElement('div');
    containerElement.id = containerId;
    containerElement.style.width = '100%';
    containerElement.style.height = '100%';
    containerElement.style.position = 'absolute';
    containerElement.style.top = '0';
    containerElement.style.left = '0';
    containerElement.style.backgroundColor = 'white';
    
    chartContainerRef.current.appendChild(containerElement);
    
    // Verify the element exists in the DOM before proceeding
    const verifyAndInitialize = () => {
      const element = document.getElementById(containerId);
      if (!element) {
        console.error(`❌ Chart container element not found: ${containerId}`);
        return;
      }
      
      console.log(`✅ Chart container element verified: ${containerId}`);
      initializeChart();
    };
    
    // Add a small delay to ensure the DOM is ready
    const initializeChart = () => {
      // Double-check that the container element still exists
      const element = document.getElementById(containerId);
      if (!element) {
        console.error(`❌ Chart container element disappeared: ${containerId}`);
        return;
      }
      
      console.log(`📈 AdvancedCharts: Creating TradingView widget with symbol: ${symbolToUse}, container: ${containerId}`);
      const widgetOptions: ChartingLibraryWidgetOptions = {
        symbol: symbolToUse,
        datafeed: customDatafeed,
        interval: timeframeToResolution(currentTimeframe), // Use current timeframe
        container: containerId,
        library_path: '/charting_library/',
        locale: 'en',
        disabled_features: [
          'use_localstorage_for_settings',
          'header_compare',
          'header_screenshot',
          'border_around_the_chart',
          'volume_force_overlay',
          'create_volume_indicator_by_default'
        ],
        enabled_features: [
          'save_chart_properties_to_local_storage',
          'remove_library_container_border',
          'left_toolbar',
          'right_toolbar',
          'control_bar',
          'seconds_resolution',
          'constraint_dialogs_movement',
          'header_symbol_search'
        ],
        charts_storage_url: 'https://saveload.tradingview.com',
        charts_storage_api_version: '1.1',
        client_id: 'ekko.trade',
        user_id: symbolToUse || 'anonymous',
        fullscreen: false,
        autosize: true,
        theme: 'Dark',
        overrides: {
          "mainSeriesProperties.style": 1, // Candles
          "mainSeriesProperties.candleStyle.upColor": "#24c35b",
          "mainSeriesProperties.candleStyle.downColor": "#ef5350",
          "mainSeriesProperties.candleStyle.drawWick": true,
          "mainSeriesProperties.candleStyle.drawBorder": true,
          "mainSeriesProperties.candleStyle.borderUpColor": "#24c35b",
          "mainSeriesProperties.candleStyle.borderDownColor": "#ef5350",
          "mainSeriesProperties.candleStyle.wickUpColor": "#24c35b",
          "mainSeriesProperties.candleStyle.wickDownColor": "#ef5350",
          "mainSeriesProperties.candleStyle.barColorsOnPrevClose": false,
          "mainSeriesProperties.candleStyle.bodyVisible": true,
          "paneProperties.background": "#16161a",
          "paneProperties.backgroundType": "solid",
          "paneProperties.backgroundGradientStartColor": "#16161a",
          "paneProperties.backgroundGradientEndColor": "#16161a"
        },
        custom_css_url: '/charting_library/custom-styles.css',
        loading_screen: {
          backgroundColor: "white",
          foregroundColor: "#000000"
        },
        time_frames: [  // Define available timeframes
          { text: "1s", resolution: "1S", description: "1 Second" },
          { text: "5s", resolution: "5S", description: "5 Seconds" },
          { text: "15s", resolution: "15S", description: "15 Seconds" },
          { text: "30s", resolution: "30S", description: "30 Seconds" },
          { text: "1m", resolution: "1", description: "1 Minute" },
          { text: "5m", resolution: "5", description: "5 Minutes" },
          { text: "15m", resolution: "15", description: "15 Minutes" },
          { text: "30m", resolution: "30", description: "30 Minutes" },
          { text: "1h", resolution: "60", description: "1 Hour" },
          { text: "4h", resolution: "240", description: "4 Hours" },
          { text: "1d", resolution: "D", description: "1 Day" },
          { text: "1W", resolution: "1W", description: "1 Week" },
        ],
        studies_overrides: {
          "volume.volume.color.0": "#ef5350", // Red for down volume
          "volume.volume.color.1": "#24c35b", // Green for up volume
          "volume.precision": 0
        },
        // Set higher priority for chart data loading
        datafeed_management: {
          priority: 'high'
        },
      };
      
      try {
        // Final verification before widget creation
        const finalCheck = document.getElementById(containerId);
        if (!finalCheck) {
          throw new Error(`Container element ${containerId} not found during widget creation`);
        }
        
        // Create new widget instance
        console.log(`🔧 Creating TradingView widget instance for container: ${containerId}`);
        const widget = new window.TradingView.widget(widgetOptions);
        
        // On chart ready callback
        widget.onChartReady(() => {
          console.log('✅ TradingView chart is ready and loaded successfully!');
          setChartInstance(widget);
          
          // Remove preloader
          const preloader = chartContainerRef.current?.querySelector('div[style*="position: absolute"]');
          if (preloader && preloader.innerHTML.includes('Loading Chart')) {
            preloader.remove();
            console.log('🗑️ Removed chart preloader');
          }
          
          // Add position lines if we have a position
          if (userPosition) {
            setTimeout(() => {
              addPositionLinesToChart(userPosition);
            }, 2000); // Delay to ensure chart is fully initialized
          }
          
          // Apply persistent styling - WHITE THEME VERSION
          const applyPersistentStyling = () => {
            try {
              console.log('🎨 Applying persistent chart styling (white theme)...');
              
              // 1. Apply CSS custom properties for white theme with BLACK toolbar elements
              widget.setCSSCustomProperty('--tv-color-pane-background', 'white');
              widget.setCSSCustomProperty('--tv-color-platform-background', 'white');
              widget.setCSSCustomProperty('--tv-color-pane-background-secondary', 'white');
              widget.setCSSCustomProperty('--tv-color-toolbar-button-background-hover', '#f3f4f6');
              
              // Force toolbar text and icons to be BLACK
              widget.setCSSCustomProperty('--tv-color-toolbar-button-text', '#000000');
              widget.setCSSCustomProperty('--tv-color-toolbar-button-text-hover', '#000000');
              widget.setCSSCustomProperty('--tv-color-toolbar-button-text-active', '#000000');
              widget.setCSSCustomProperty('--tv-color-toolbar-button-text-active-hover', '#000000');
              widget.setCSSCustomProperty('--tv-color-popup-element-text', '#000000');
              widget.setCSSCustomProperty('--tv-color-popup-element-text-hover', '#000000');
              widget.setCSSCustomProperty('--tv-color-popup-element-secondary-text', '#000000');
              widget.setCSSCustomProperty('--tv-color-item-active-text', '#000000');
              
              // 2. Inject persistent CSS styles directly into ALL chart iframes
              const chartFrames = document.querySelectorAll('iframe[id*="tradingview"], iframe[src*="tradingview"]') as NodeListOf<HTMLIFrameElement>;
              chartFrames.forEach((chartFrame, index) => {
                try {
                  if (chartFrame && chartFrame.contentDocument) {
                    const frameDoc = chartFrame.contentDocument;
                    let styleElement = frameDoc.getElementById('ekko-persistent-styles');
                    
                    if (!styleElement) {
                      styleElement = frameDoc.createElement('style');
                      styleElement.id = 'ekko-persistent-styles';
                      styleElement.innerHTML = `
                        /* EKKO Persistent Chart Styles - White Theme */
                        .layout__area--center, 
                        .chart-container__chartarea, 
                        .chart-container,
                        .tv-chart-container,
                        .chart-markup-table,
                        .chart-page,
                        .chart-container-border,
                        .chart-gui-wrapper,
                        .chart-widget,
                        .trading-ui-chart,
                        .chart-page__container,
                        div[data-name="legend-source-item"] {
                          background-color: white !important;
                        }
                        
                        /* Toolbar styling - white background with BLACK icons and text */
                        .toolbar-button,
                        .toolbar-button__content,
                        .control-bar,
                        .control-bar__group,
                        .control-bar-wrapper,
                        .control-bar-wrapper__content,
                        div[class*="toolbar"],
                        div[class*="button"],
                        .tv-toolbar,
                        .tv-control-bar,
                        .left-toolbar,
                        .right-toolbar {
                          background-color: white !important;
                          border-color: #e5e7eb !important;
                          color: #000000 !important;
                        }
                        
                        /* AGGRESSIVE toolbar icons and text styling - MAKE EVERYTHING BLACK */
                        .toolbar-button svg,
                        .toolbar-button path,
                        .toolbar-button__icon,
                        .toolbar-button__text,
                        .control-bar svg,
                        .control-bar path,
                        .control-bar__icon,
                        .control-bar__text,
                        .tv-toolbar svg,
                        .tv-toolbar path,
                        .tv-toolbar__icon,
                        .tv-toolbar__text,
                        div[class*="toolbar"] svg,
                        div[class*="toolbar"] path,
                        div[class*="button"] svg,
                        div[class*="button"] path,
                        .left-toolbar svg,
                        .left-toolbar path,
                        .right-toolbar svg,
                        .right-toolbar path,
                        [class*="toolbar"] [class*="icon"],
                        [class*="toolbar"] [class*="button"],
                        [data-name*="toolbar"] svg,
                        [data-name*="toolbar"] path,
                        [data-name*="button"] svg,
                        [data-name*="button"] path {
                          fill: #000000 !important;
                          color: #000000 !important;
                          stroke: #000000 !important;
                          opacity: 1 !important;
                        }
                        
                        /* Target TradingView specific classes */
                        [class*="tv-"] svg,
                        [class*="tv-"] path,
                        [id*="tv-"] svg,
                        [id*="tv-"] path,
                        .tv-chart-container svg,
                        .tv-chart-container path {
                          fill: #000000 !important;
                          color: #000000 !important;
                          stroke: #000000 !important;
                          opacity: 1 !important;
                        }
                        
                        /* Nuclear option - target ALL SVGs in chart areas */
                        .chart-container svg,
                        .chart-container path,
                        .chart-gui-wrapper svg,
                        .chart-gui-wrapper path,
                        iframe svg,
                        iframe path {
                          fill: #000000 !important;
                          color: #000000 !important;
                          stroke: #000000 !important;
                          opacity: 1 !important;
                        }
                        
                        .toolbar-button:hover,
                        .toolbar-button__content:hover {
                          background-color: #f9fafb !important;
                          border-color: #e5e7eb !important;
                          color: #000000 !important;
                        }
                        
                        .toolbar-button:hover svg,
                        .toolbar-button:hover path,
                        .toolbar-button:hover .toolbar-button__icon,
                        .toolbar-button:hover .toolbar-button__text {
                          fill: #000000 !important;
                          color: #000000 !important;
                          stroke: #000000 !important;
                          opacity: 1 !important;
                        }
                        
                        /* Prevent dark flashes */
                        body, html, #app, .tv-chart-container__wrapper {
                          background-color: white !important;
                        }
                        
                        /* Force white theme colors with BLACK toolbar text */
                        :root {
                          --tv-color-pane-background: white !important;
                          --tv-color-platform-background: white !important;
                          --tv-color-pane-background-secondary: white !important;
                          --tv-color-toolbar-button-background-hover: #f3f4f6 !important;
                          --tv-color-toolbar-button-text: #000000 !important;
                          --tv-color-toolbar-button-text-hover: #000000 !important;
                          --tv-color-toolbar-button-text-active: #000000 !important;
                          --tv-color-toolbar-button-text-active-hover: #000000 !important;
                          --tv-color-popup-element-text: #000000 !important;
                          --tv-color-popup-element-text-hover: #000000 !important;
                          --tv-color-popup-element-secondary-text: #000000 !important;
                          --tv-color-item-active-text: #000000 !important;
                        }
                      `;
                      frameDoc.head.appendChild(styleElement);
                      console.log(`✅ Injected persistent white theme styles into chart iframe ${index + 1}`);
                    }
                  }
                } catch (err) {
                  console.warn(`⚠️ Could not inject styles into iframe ${index + 1}:`, err);
                }
              });
              
              // 3. Apply chart-specific overrides for white theme
              const chart = widget.chart();
              if (chart) {
                console.log('📊 Applying chart-specific styling (white theme)...');
                
                // Set candlestick as the default chart type
                chart.setChartType(1); // 1 = Candlesticks
                
                // Apply comprehensive overrides for white theme - MORE AGGRESSIVE
                const whiteThemeOverrides = {
                  "paneProperties.background": "white",
                  "paneProperties.backgroundType": "solid",
                  "paneProperties.backgroundGradientStartColor": "white",
                  "paneProperties.backgroundGradientEndColor": "white",
                  "paneProperties.vertGridProperties.color": "#f0f0f0",
                  "paneProperties.horzGridProperties.color": "#f0f0f0",
                  "scalesProperties.backgroundColor": "white",
                  "scalesProperties.lineColor": "#e0e0e0",
                  "scalesProperties.textColor": "#666666",
                  "mainSeriesProperties.candleStyle.upColor": "#24c35b",
                  "mainSeriesProperties.candleStyle.downColor": "#ef4444",
                  "mainSeriesProperties.candleStyle.borderUpColor": "#24c35b",
                  "mainSeriesProperties.candleStyle.borderDownColor": "#ef4444",
                  "mainSeriesProperties.candleStyle.wickUpColor": "#24c35b",
                  "mainSeriesProperties.candleStyle.wickDownColor": "#ef4444",
                  "timeScale.rightOffset": 12,
                  "timeScale.barSpacing": 6
                };
                
                chart.applyOverrides(whiteThemeOverrides);
                
                // Store overrides for reapplication
                (widget as any)._ekkoOverrides = whiteThemeOverrides;
                
                // Set price format
                chart.setPriceFormat({
                  type: 'price',
                  precision: 8,
                  minMove: 0.00000001
                });
                
                // Force chart redraw with new settings
                try {
                  chart.resetData();
                } catch (err) {
                  console.warn('Could not reset chart data:', err);
                }
              }
              
              // 4. Apply styles to the main container
              const chartContainer = document.getElementById(containerId);
              if (chartContainer) {
                chartContainer.style.backgroundColor = 'white';
                chartContainer.style.border = 'none';
              }
              
              console.log('✅ Persistent white theme styling applied successfully');
            } catch (err) {
              console.warn('⚠️ Error applying persistent styling:', err);
            }
          };
          
          // Apply styling immediately
          applyPersistentStyling();
          
          // Reapply styling more frequently to handle chart redraws and widget moves
          const styleInterval = setInterval(() => {
            if (chartInstance) {
              applyPersistentStyling();
            } else {
              clearInterval(styleInterval);
            }
          }, 500); // Reapply every 500ms for better persistence
          
          // Store the interval for cleanup
          (widget as any)._ekkoStyleInterval = styleInterval;
          
          // Additional immediate reapplication after short delays
          setTimeout(() => applyPersistentStyling(), 100);
          setTimeout(() => applyPersistentStyling(), 300);
          setTimeout(() => applyPersistentStyling(), 1000);
          setTimeout(() => applyPersistentStyling(), 2000);
          
                      // Add MutationObserver to detect chart DOM changes and reapply styles
            const chartContainer = document.getElementById(containerId);
            if (chartContainer) {
              const mutationObserver = new MutationObserver((mutations) => {
                let shouldReapplyStyles = false;
                
                mutations.forEach((mutation) => {
                  // Check if the chart container or its children have been modified
                  if (mutation.type === 'childList' || mutation.type === 'attributes') {
                    // Look for TradingView-specific changes
                    const target = mutation.target as Element;
                    if (target.id?.includes('tradingview') || 
                        target.className?.includes('chart') ||
                        target.className?.includes('tv-') ||
                        mutation.addedNodes.length > 0) {
                      shouldReapplyStyles = true;
                    }
                  }
                });
                
                if (shouldReapplyStyles) {
                  console.log('🔄 Chart DOM changed, reapplying styles...');
                  setTimeout(applyPersistentStyling, 50);
                  setTimeout(applyPersistentStyling, 200);
                  setTimeout(applyPersistentStyling, 500);
                }
              });
              
              // Observe the chart container and its subtree
              mutationObserver.observe(chartContainer, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['style', 'class', 'id']
              });
              
              // Store the observer for cleanup
              (widget as any)._ekkoMutationObserver = mutationObserver;
              console.log('✅ MutationObserver set up for chart styling persistence');
            }
            
            // Add window resize and focus listeners to reapply styles
            const handleReapplyStyles = () => {
              console.log('🔄 Window event triggered, reapplying chart styles...');
              applyPersistentStyling();
            };
            
            window.addEventListener('resize', handleReapplyStyles);
            window.addEventListener('focus', handleReapplyStyles);
            
            // Store listeners for cleanup
            (widget as any)._ekkoEventListeners = [
              { type: 'resize', handler: handleReapplyStyles },
              { type: 'focus', handler: handleReapplyStyles }
            ];
          
          // Force the chart to request data by setting the symbol again
          setTimeout(() => {
            console.log('🔄 Forcing chart to refresh data...');
            try {
              const chart = widget.chart();
              if (chart) {
                // Get the current symbol and force a refresh
                chart.setSymbol(symbolToUse, () => {
                  console.log('✅ Chart symbol set and data requested');
                  // Reapply styling after symbol change
                  setTimeout(applyPersistentStyling, 500);
                });
              }
            } catch (err) {
              console.warn('⚠️ Error forcing chart refresh:', err);
            }
          }, 500);
        });
        
        // Add error handling for widget creation
        widget.onError = (error: any) => {
          console.error('❌ TradingView widget error:', error);
        };
        
      } catch (err) {
        console.error('❌ Error initializing TradingView chart:', err);
        
        // Show error message in the chart container
        if (chartContainerRef.current) {
          chartContainerRef.current.innerHTML = `
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; background: #16161a; color: #ef4444;">
              <div style="font-size: 16px; margin-bottom: 10px;">⚠️ Chart Loading Error</div>
              <div style="font-size: 12px; color: #8f8f8f; text-align: center; max-width: 300px;">
                ${err instanceof Error ? err.message : 'Unknown error occurred while loading the chart'}
              </div>
              <div style="font-size: 10px; color: #6b7280; margin-top: 10px;">
                Container ID: ${containerId}
              </div>
              <button onclick="window.location.reload()" style="margin-top: 10px; padding: 5px 10px; background: #24c35b; color: white; border: none; border-radius: 4px; cursor: pointer;">
                Reload Page
              </button>
            </div>
          `;
        }
      }
    };
    
    // Initialize chart with verification and delay to ensure DOM is ready
    setTimeout(verifyAndInitialize, 100);
    
    // Cleanup on unmount
    return () => {
      // Clear position lines before removing chart
      clearPositionLines();
      
      if (chartInstance) {
        try {
          // Clear the style interval if it exists
          if ((chartInstance as any)._ekkoStyleInterval) {
            clearInterval((chartInstance as any)._ekkoStyleInterval);
            console.log('🧹 Cleared style reapplication interval');
          }
          
          // Disconnect the MutationObserver if it exists
          if ((chartInstance as any)._ekkoMutationObserver) {
            (chartInstance as any)._ekkoMutationObserver.disconnect();
            console.log('🧹 Disconnected MutationObserver');
          }
          
          // Clean up event listeners if they exist
          if ((chartInstance as any)._ekkoEventListeners) {
            (chartInstance as any)._ekkoEventListeners.forEach(({ type, handler }: any) => {
              window.removeEventListener(type, handler);
            });
            console.log('🧹 Cleaned up window event listeners');
          }
          
          // Remove the chart instance
          chartInstance.remove();
          console.log('🧹 Chart instance removed');
        } catch (err) {
          console.error('Error cleaning up chart instance:', err);
        }
      }
    };
  }, [scriptsLoaded, currentTimeframe, timeframeToResolution, effectiveToken?.mint, hasInitialData]);
  
  // Fetch chart data for token and timeframe
  const fetchChartData = async (tokenMint: string, tf: string, retryCount = 0) => {
    console.log(`📊 AdvancedCharts: Starting fetchChartData for ${tokenMint}, timeframe: ${tf}, retry: ${retryCount}`);
    setIsLoading(true);
    
    try {
      // Calculate time range based on timeframe - use appropriate periods for each timeframe
      const now = Math.floor(Date.now() / 1000);
      let startTime;
      
      // Use appropriate time ranges based on timeframe
      switch (tf) {
        case '1s':
        case '5s':
        case '15s':
        case '30s':
        startTime = now - (24 * 60 * 60); // 24 hours for second-level timeframes
          break;
        case '1m':
        case '5m':
        case '15m':
        startTime = now - (7 * 24 * 60 * 60); // 7 days for minute-level timeframes
          break;
        case '30m':
        case '1h':
        case '2h':
        case '4h':
        startTime = now - (30 * 24 * 60 * 60); // 30 days for hour-level timeframes
          break;
        default:
        startTime = now - (90 * 24 * 60 * 60); // 90 days for daily/weekly timeframes
          break;
      }
      
      console.log(`📈 AdvancedCharts: Fetching data for ${tokenMint}, timeframe: ${tf}, from: ${new Date(startTime * 1000).toISOString()} to: ${new Date(now * 1000).toISOString()}`);
      
      const result = await getTokenChartData(tokenMint, tf, startTime, now);
      console.log(`📊 AdvancedCharts: getTokenChartData result for ${tokenMint}:`, {
        status: result.status,
        dataLength: result.data?.length || 0,
        error: result.error,
        sampleData: result.data?.[0]
      });
      
      if (result.status === 'success' && result.data && result.data.length > 0) {
        console.log(`✅ AdvancedCharts: Received ${result.data.length} data points for timeframe ${tf}`);
        
        // Set data in state
        setChartData(result.data);
        setHasInitialData(true); // Mark that we have data
        console.log(`📊 AdvancedCharts: Chart data set in state, sample data:`, result.data.slice(0, 3));
        
        // Calculate price stats
        const latestCandle = result.data[result.data.length - 1];
        const firstCandle = result.data[0];
        
        // Get 24h high/low
        const time24hAgo = now - 86400;
        const last24hData = result.data.filter(candle => candle.time >= time24hAgo);
        
        const high24h = Math.max(...last24hData.map(candle => candle.high));
        const low24h = Math.min(...last24hData.map(candle => candle.low));
        const volume24h = last24hData.reduce((sum, candle) => sum + candle.volume, 0);
        
        // Calculate price change percentage
        const priceChange = ((latestCandle.close - firstCandle.open) / firstCandle.open) * 100;
        
        setPriceStats({
          current: latestCandle.close,
          change: priceChange,
          high24h,
          low24h,
          volume24h
        });
      } else {
        console.warn(`⚠️ AdvancedCharts: No chart data returned for ${tokenMint}:`, result);
        
        // Try different timeframes if no data found and this is the first retry
        if (retryCount === 0) {
          console.log(`🔄 AdvancedCharts: Retrying with different timeframes for ${tokenMint}`);
          // Use timeframes that are more likely to have data, starting with longer periods
          const alternativeTimeframes = ['1d', '1h', '5m', '1m', '15m', '30m'];
          
          for (const altTf of alternativeTimeframes) {
            if (altTf !== tf) {
              console.log(`🔄 AdvancedCharts: Trying timeframe ${altTf} for ${tokenMint}`);
              
              // Use appropriate time ranges for each timeframe
              let altStartTime;
              if (altTf === '1d') {
                altStartTime = now - (90 * 24 * 60 * 60); // 90 days for daily
              } else if (altTf === '1h' || altTf === '2h' || altTf === '4h') {
                altStartTime = now - (30 * 24 * 60 * 60); // 30 days for hourly
              } else {
                altStartTime = now - (7 * 24 * 60 * 60); // 7 days for minutes
              }
              
              const altResult = await getTokenChartData(tokenMint, altTf, altStartTime, now);
              
              if (altResult.status === 'success' && altResult.data && altResult.data.length > 0) {
                console.log(`✅ AdvancedCharts: Found data with timeframe ${altTf} for ${tokenMint}`);
                setChartData(altResult.data);
                setHasInitialData(true);
                setCurrentTimeframe(altTf); // Update to the working timeframe
                
                // Calculate price stats with the alternative data
                const latestCandle = altResult.data[altResult.data.length - 1];
                const firstCandle = altResult.data[0];
                const priceChange = ((latestCandle.close - firstCandle.open) / firstCandle.open) * 100;
                
                setPriceStats({
                  current: latestCandle.close,
                  change: priceChange,
                  high24h: Math.max(...altResult.data.map(candle => candle.high)),
                  low24h: Math.min(...altResult.data.map(candle => candle.low)),
                  volume24h: altResult.data.reduce((sum, candle) => sum + candle.volume, 0)
                });
                
                return; // Exit early since we found data
              }
            }
          }
        }
        
        // If we still have no data, set empty state
        setChartData([]);
        setHasInitialData(false);
        setPriceStats({
          current: 0,
          change: 0,
          high24h: 0,
          low24h: 0,
          volume24h: 0
        });
        
        // Retry once more after a delay if this is the first attempt
        if (retryCount < 1) {
          console.log(`🔄 AdvancedCharts: Retrying data fetch for ${tokenMint} in 3 seconds...`);
          setTimeout(() => {
            fetchChartData(tokenMint, tf, retryCount + 1);
          }, 3000);
        }
      }
    } catch (error) {
      console.error(`❌ AdvancedCharts: Error fetching chart data for ${tokenMint}, timeframe ${tf}:`, error);
      setChartData([]);
      setHasInitialData(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle timeframe button click
  const handleTimeframeChange = (tf: string) => {
    console.log(`🔄 [AdvancedCharts] Timeframe change requested: ${currentTimeframe} -> ${tf}`);
    
    const previousTimeframe = currentTimeframe;
    setCurrentTimeframe(tf);
    
    if (effectiveToken?.mint) {
      console.log(`🔄 [AdvancedCharts] Fetching chart data for timeframe change: ${effectiveToken.mint}, ${tf}`);
      
      // Force chart to reinitialize with new timeframe
      if (chartInstance) {
        console.log(`🔄 [AdvancedCharts] Chart instance exists, will trigger TradingView timeframe change`);
        
        // TradingView will automatically call unsubscribeBars for old timeframe and subscribeBars for new timeframe
        // We just need to ensure our data is ready
        fetchChartData(effectiveToken.mint, tf);
      } else {
        console.log(`🔄 [AdvancedCharts] No chart instance, fetching data for future initialization`);
        fetchChartData(effectiveToken.mint, tf);
      }
    } else {
      console.warn(`🔄 [AdvancedCharts] No effective token for timeframe change`);
    }
    
    console.log(`🔄 [AdvancedCharts] Timeframe change completed: ${previousTimeframe} -> ${tf}`);
  };

    const customStyles = `
    /* Chart tool button styles - white theme */
    .chart-tool-button {
      background-color: white;
      border: 1px solid #e5e7eb;
      color: #24c35b;
    }
    
    .chart-tool-button:hover {
      background-color: #f9fafb;
      border-color: #24c35b;
    }
    
    /* Force chart container to fill all available space - white background */
    .chart-container-wrapper {
      height: 100% !important;
      flex: 1 1 auto !important;
      display: flex !important;
      flex-direction: column !important;
      min-height: 0 !important;
      background-color: white !important;
    }
    
    /* Prevent flash of grey during loading - white background */
    #tv_chart_container {
      background-color: white !important;
    }
    
    /* TradingView component containers - white theme */
    .layout__area--center, 
    .chart-container__chartarea, 
    .chart-container,
    .tv-chart-container,
    .chart-markup-table,
    .chart-page,
    .chart-container-border,
    .chart-gui-wrapper {
      background-color: white !important;
    }
    
    /* Additional background overrides - white theme */
    :root {
      --tv-color-pane-background: white !important;
      --tv-color-platform-background: white !important;
      --tv-color-pane-background-secondary: white !important;
      --tv-color-toolbar-button-background-hover: #f3f4f6 !important;
    }
    
    /* Force chart background color - white theme */
    .chart-widget,
    .trading-ui-chart,
    .chart-container-border,
    ._tv-dialog {
      background-color: white !important;
    }
  `;

  const parentResizeObserver = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    // Use ResizeObserver to handle parent/container resizes
    if (!chartContainerRef.current) return;
    if (!chartInstance) return;

    if (parentResizeObserver.current) {
      parentResizeObserver.current.disconnect();
    }

    parentResizeObserver.current = new ResizeObserver(entries => {
      for (let entry of entries) {
        if (entry.contentRect && chartInstance && typeof chartInstance.resize === 'function') {
          chartInstance.resize(entry.contentRect.width, entry.contentRect.height);
        }
      }
    });
    parentResizeObserver.current.observe(chartContainerRef.current);

    return () => {
      if (parentResizeObserver.current) {
        parentResizeObserver.current.disconnect();
      }
    };
  }, [chartInstance]);

  const renderedChart = (
    <div className="w-full h-full bg-transparent relative flex-grow flex flex-col">
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="flex flex-col items-center gap-3">
            <img 
              src="/src/assets/images/EkkoLogo3.png" 
              alt="EKKO" 
              className="h-12 opacity-80"
              style={{ filter: 'brightness(0)', objectFit: 'contain' }} // Makes the logo black and maintains aspect ratio
            />
            <div className="flex items-center gap-2">
              <RefreshCcw size={16} className="text-[#24c35b] animate-spin" />
              <span className="text-black text-sm">Loading chart data...</span>
            </div>
          </div>
        </div>
      ) : !hasInitialData && effectiveToken ? (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="flex flex-col items-center gap-3">
            <img 
              src="/src/assets/images/EkkoLogo3.png" 
              alt="EKKO" 
              className="h-10 opacity-60"
              style={{ filter: 'brightness(0)', objectFit: 'contain' }} // Makes the logo black and maintains aspect ratio
            />
            <BarChart size={24} className="text-gray-400 opacity-50" />
            <div className="text-center">
              <p className="text-black text-sm mb-1">No chart data available</p>
              <p className="text-gray-600 text-xs">This token may not have trading history yet</p>
            </div>
            <button
              onClick={() => {
                if (effectiveToken?.mint) {
                  setHasInitialData(false);
                  fetchChartData(effectiveToken.mint, currentTimeframe);
                }
              }}
              className="mt-2 px-3 py-1.5 bg-[#24c35b]/20 hover:bg-[#24c35b]/30 text-[#24c35b] rounded text-xs transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        effectiveToken ? (
          <div 
            ref={chartContainerRef} 
            className="chart-container flex-1" 
            style={{ 
              width: '100%', 
              height: '100%', 
              backgroundColor: 'white'
            }}
          />
        ) : (
          <div className="text-center text-gray-600 flex flex-col items-center justify-center h-full">
            <img 
              src="/src/assets/images/EkkoLogo3.png" 
              alt="EKKO" 
              className="h-16 mb-4 opacity-40"
              style={{ filter: 'brightness(0)', objectFit: 'contain' }} // Makes the logo black and maintains aspect ratio
            />
            <TrendingUp size={32} className="mb-4 opacity-30 text-gray-400" />
            <p className="text-lg mb-2 text-black">No token selected</p>
            <button
              onClick={() => setIsSearchModalOpen(true)}
              className="mt-4 px-4 py-2 bg-[#24c35b]/20 hover:bg-[#24c35b]/30 text-[#24c35b] rounded-md text-sm transition-colors flex items-center"
            >
              <Search size={14} className="mr-2" />
              Search for a token
            </button>
          </div>
        )
      )}
    </div>
  );

  const content = (
    <div 
      className="flex flex-col bg-white h-full w-full rounded-xl overflow-hidden shadow-sm border border-gray-200" 
      style={{ 
        height: '100%', 
        overflow: 'hidden',
        backgroundColor: 'white'
      }} 
      data-widget-type="advanced-charts"
    >
      <style>{customStyles}</style>
      
      {/* Header with standardized widget title background */}
      <div className="p-3 border-b border-gray-200 bg-white rounded-t-xl">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
            <LineChart size={16} className="text-[#24c35b] mr-2" />
            {effectiveToken && (
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-black">{effectiveToken.symbol}</span>
                <span className="text-xs text-gray-600 ml-1">({(effectiveToken.name || 'Unknown Token').slice(0, 18)}{(effectiveToken.name || '').length > 18 ? '...' : ''})</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(effectiveToken.mint || '');
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1200);
                  }}
                  className={`ml-1 transition-colors ${copied ? 'text-[#24c35b]' : 'text-gray-500 hover:text-gray-700'}`}
                  title={copied ? 'Copied!' : 'Copy token address'}
                >
                  <Copy size={14} />
                </button>
                {(priceStats.current > 0 || tokenMarketData?.price?.usd) && (
                  <div className="flex items-center ml-4">
                    <span className="font-bold text-sm text-black mr-2">
                      {tokenMarketData?.price?.usd ? formatPrice(tokenMarketData.price.usd) : formatPrice(priceStats.current)}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      (tokenMarketData?.priceChange?.['24h'] ?? priceStats.change) >= 0 
                        ? 'bg-[#24c35b]/20 text-[#24c35b]' 
                        : 'bg-red-500/20 text-red-500'
                    }`}>
                      {(tokenMarketData?.priceChange?.['24h'] ?? priceStats.change) >= 0 ? '+' : ''}
                      {(tokenMarketData?.priceChange?.['24h'] ?? priceStats.change).toFixed(2)}%
                    </span>
                  </div>
                )}
                {/* Real-time market data */}
                <div className="flex items-center gap-3 text-xs ml-4">
                  <div className="flex flex-col items-center">
                    <span className="text-gray-600 text-[10px] leading-none">Liq</span>
                    <span className="text-black font-medium text-xs leading-tight">
                      {tokenMarketData ? formatCompactNumber(tokenMarketData.liquidity.usd || 0) : '-'}
                    </span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-gray-600 text-[10px] leading-none">MCap</span>
                    <span className="text-black font-medium text-xs leading-tight">
                      {tokenMarketData ? formatCompactNumber(tokenMarketData.marketCap.usd || 0) : '-'}
                    </span>
                  </div>
                  {(tokenMarketData?.allTimeVolume || priceStats.volume24h) > 0 && (
                    <div className="flex flex-col items-center">
                      <span className="text-gray-600 text-[10px] leading-none">All Vol</span>
                      <span className="text-black font-medium text-xs leading-tight">
                        {formatVolume(tokenMarketData?.allTimeVolume || priceStats.volume24h)}
                      </span>
                    </div>
                  )}
                  {tokenMarketData?.holders && tokenMarketData.holders > 0 && (
                    <div className="flex flex-col items-center">
                      <span className="text-gray-600 text-[10px] leading-none">Holders</span>
                      <span className="text-black font-medium text-xs leading-tight">
                        {formatHolders(tokenMarketData.holders)}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col items-center">
                    <span className="text-gray-600 text-[10px] leading-none">Market</span>
                    <span className="text-black font-medium text-xs leading-tight">
                      {tokenMarketData?.market || '-'}
                    </span>
                  </div>
                  {/* Security indicators */}
                  {tokenMarketData?.security && (
                    <div className="flex flex-col items-center">
                      <span className="text-gray-400 text-[10px] leading-none">Security</span>
                      <div className="flex items-center gap-1">
                        {tokenMarketData.security.freezeAuthority === null && (
                          <span className="text-[10px] px-1 py-0.5 bg-green-500/20 text-green-400 rounded" title="Freeze authority revoked">
                            🔓
                          </span>
                        )}
                        {tokenMarketData.security.mintAuthority === null && (
                          <span className="text-[10px] px-1 py-0.5 bg-green-500/20 text-green-400 rounded" title="Mint authority revoked">
                            🚫
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Position Information */}
                {/* {userPosition && (
                  <div className="flex items-center gap-2 ml-4 px-2 py-1 bg-[#24c35b]/10 border border-[#24c35b]/30 rounded">
                    <div className="flex items-center gap-1">
                      <TrendingUp size={12} className="text-[#24c35b]" />
                      <span className="text-[10px] text-gray-400">Position:</span>
                      <span className="text-xs font-medium text-white">
                        {userPosition.currentPosition.toLocaleString()} {effectiveToken?.symbol}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign size={12} className="text-[#24c35b]" />
                      <span className="text-[10px] text-gray-400">Avg:</span>
                      <span className="text-xs font-medium text-white">
                        ${userPosition.averageEntryPrice.toFixed(6)}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowClosePositionModal(true)}
                      className="px-2 py-0.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-[10px] transition-colors flex items-center gap-1"
                      title="Close position"
                    >
                      <TrendingDown size={10} />
                      Close
                    </button>
                  </div>
                )} */}
                
                {/* Social Icons */}
                {(effectiveToken || tokenMarketData) && (
                  <div className="flex items-center gap-2 ml-2">
                    {/* Twitter/X */}
                    {(() => {
                      const twitterUrl = tokenMarketData?.socials?.twitter || effectiveToken?.twitter;
                      const formattedUrl = formatSocialUrl(twitterUrl, 'twitter');
                      return formattedUrl && (
                        <a 
                          href={formattedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-[#24c35b] transition-colors"
                          title="Twitter/X"
                        >
                          <Twitter size={14} />
                        </a>
                      );
                    })()}
                    {/* Website */}
                    {(() => {
                      const websiteUrl = tokenMarketData?.socials?.website || effectiveToken?.website;
                      const formattedUrl = formatSocialUrl(websiteUrl, 'website');
                      return formattedUrl && (
                        <a 
                          href={formattedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-[#24c35b] transition-colors"
                          title="Website"
                        >
                          <Globe size={14} />
                        </a>
                      );
                    })()}
                    {/* Telegram */}
                    {tokenMarketData?.socials?.telegram && (
                      <a 
                        href={tokenMarketData.socials.telegram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-[#24c35b] transition-colors"
                        title="Telegram"
                      >
                        <MessageCircle size={14} />
                      </a>
                    )}
                    {/* Discord */}
                    {tokenMarketData?.socials?.discord && (
                      <a 
                        href={tokenMarketData.socials.discord}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-[#24c35b] transition-colors"
                        title="Discord"
                      >
                        <MessageCircle size={14} />
                      </a>
                    )}
                    {/* YouTube */}
                    {tokenMarketData?.socials?.youtube && (
                      <a 
                        href={tokenMarketData.socials.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-[#47be8f] transition-colors"
                        title="YouTube"
                      >
                        <Youtube size={14} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              ref={searchButtonRef}
              onClick={() => setIsSearchModalOpen(true)}
              className="p-1.5 rounded bg-[#24c35b]/20 hover:bg-[#24c35b]/30 text-[#24c35b] transition-colors flex items-center"
            >
              <Search size={14} className="mr-1" />
              <span className="text-xs">Search Token</span>
            </button>
            
            <button
              onClick={() => {
                if (effectiveToken?.mint) {
                  // Refresh both chart data and market data
                  fetchChartData(effectiveToken.mint, currentTimeframe);
                  fetchTokenMarketData(effectiveToken.mint);
                }
              }}
              disabled={!effectiveToken || isLoading || isLoadingMarketData}
              className="p-1 rounded hover:bg-white/5 transition-colors disabled:opacity-50"
              title="Refresh chart and market data"
            >
              <RefreshCcw size={14} className={`text-gray-400 hover:text-white ${isLoading || isLoadingMarketData ? 'animate-spin' : ''}`} />
            </button>
            
            {/* Connection status indicator */}
            <div 
              onClick={() => {
                if (!wsConnected) {
                  // Attempt to manually reconnect
                  console.log('Manually attempting to reconnect WebSocket...');
                  wsService.disconnect();
                  setTimeout(() => {
                    wsService.connect().then(connected => {
                      setWsConnected(connected);
                      if (connected && effectiveToken?.mint) {
                        // Join both potential rooms
                        const tokenRoom = `token:${effectiveToken.mint}`;
                        const priceRoom = `price:${effectiveToken.mint}`;
                        wsService.joinRoom(tokenRoom);
                        wsService.joinRoom(priceRoom);
                        // Request market data explicitly
                        wsService.send('request_token_data', { mint: effectiveToken.mint });
                        // Force data refresh
                        fetchChartData(effectiveToken.mint, currentTimeframe);
                      }
                    });
                  }, 500);
                }
              }}
              className={`p-1 rounded transition-colors text-xs flex items-center gap-1 ${
                wsConnected ? 'text-green-500' : 'text-red-500 animate-pulse cursor-pointer hover:bg-white/10'
              }`}
              title={wsConnected ? "WebSocket connected" : "WebSocket disconnected - Click to reconnect"}
            >
              {wsConnected ? (
                <Wifi size={12} />
              ) : (
                <AlertCircle size={12} />
              )}
            </div>
            
            {/* Real-time data indicator */}
            {tokenMarketData && (
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <div className={`w-2 h-2 rounded-full ${tokenMarketData ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                <span title={tokenMarketData.lastUpdated ? new Date(tokenMarketData.lastUpdated).toLocaleTimeString() : 'Market data'}>
                  {tokenMarketData ? 'LIVE' : 'OFFLINE'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Chart Content */}
      <div className="flex-1 relative">
        {renderedChart}
      </div>
      
      {/* Token Search Modal */}
      <TokenSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSelectToken={(token: Token) => {
          setLocalSelectedToken(token);
          setIsSearchModalOpen(false);
          // Also broadcast the token selection to other components
          broadcastTokenSelection(token);
        }}
        referenceElement={searchButtonRef.current}
      />
      
      {/* Close Position Modal */}
      {showClosePositionModal && userPosition && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#16161a] border border-[#24c35b]/40 rounded-lg w-full max-w-md shadow-xl">
            {/* Header */}
            <div className="p-4 border-b border-[#24c35b]/20">
              <div className="flex items-center justify-between">
                <h3 className="text-white text-lg font-semibold flex items-center">
                  <TrendingDown size={20} className="text-red-400 mr-2" />
                  Close Position
                </h3>
                <button
                  onClick={() => setShowClosePositionModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {/* Position Summary */}
              <div className="mb-4 p-3 bg-[#24c35b]/5 border border-[#24c35b]/20 rounded">
                <div className="text-sm text-gray-400 mb-2">Current Position</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-400">Amount</div>
                    <div className="text-white font-medium">
                      {userPosition.currentPosition.toLocaleString()} {effectiveToken?.symbol}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400">Avg Entry</div>
                    <div className="text-white font-medium">
                      ${userPosition.averageEntryPrice.toFixed(6)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400">Total Invested</div>
                    <div className="text-white font-medium">
                      {userPosition.totalSolInvested.toFixed(3)} SOL
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400">Current Value</div>
                    <div className="text-white font-medium">
                      {tokenMarketData?.price?.usd 
                        ? `$${(userPosition.currentPosition * tokenMarketData.price.usd).toFixed(2)}`
                        : 'N/A'
                      }
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Close Percentage */}
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">
                  Percentage to Close: {closePositionPercentage}%
                </label>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={closePositionPercentage}
                  onChange={(e) => setClosePositionPercentage(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #24c35b 0%, #24c35b ${closePositionPercentage}%, #374151 ${closePositionPercentage}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
              
              {/* Amount to Sell */}
              <div className="mb-4 p-3 bg-gray-800/30 rounded">
                <div className="text-sm text-gray-400 mb-1">Amount to Sell</div>
                <div className="text-white font-medium">
                  {((userPosition.currentPosition * closePositionPercentage) / 100).toLocaleString()} {effectiveToken?.symbol}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Estimated SOL: {tokenMarketData?.price?.usd 
                    ? `${((userPosition.currentPosition * closePositionPercentage / 100) * tokenMarketData.price.usd / (tokenMarketData.price.usd / (tokenMarketData.price.quote || 1))).toFixed(3)} SOL`
                    : 'N/A'
                  }
                </div>
              </div>
              
              {/* Quick Percentage Buttons */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[25, 50, 75, 100].map(percentage => (
                  <button
                    key={percentage}
                    onClick={() => setClosePositionPercentage(percentage)}
                    className={`py-2 px-3 rounded text-sm transition-colors ${
                      closePositionPercentage === percentage
                        ? 'bg-[#24c35b] text-black font-medium'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {percentage}%
                  </button>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-700/50 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowClosePositionModal(false)}
                className="px-4 py-2 rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleClosePosition}
                disabled={isClosingPosition}
                className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isClosingPosition ? (
                  <>
                    <RefreshCcw size={16} className="animate-spin" />
                    Closing...
                  </>
                ) : (
                  <>
                    <TrendingDown size={16} />
                    Close {closePositionPercentage}%
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">
        {content}
      </div>
    );
  }

  // Otherwise, return the modal version with container
  return (
    <div className="p-4 bg-dark-grey border border-[#24c35b]/40 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp size={18} className="text-primary" />
          <h2 className="text-lg font-bold text-white">Advanced Charts</h2>
        </div>
        {/* {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 rounded hover:bg-gray-700/20"
            title="Close"
          >
            <X size={16} />
          </button>
        )} */}
      </div>
      
      {content}
    </div>
  );
};

// Export a widget creator function for the canvas
export const createAdvancedChartsWidget = (selectedToken?: Token) => {
  // Create a unique key to ensure the widget is uniquely identified 
  const uniqueKey = `chart-${selectedToken?.mint || 'default'}-${Date.now()}`;
  
  return {
    type: 'advanced-charts',
    title: 'Advanced Charts',
    content: <AdvancedCharts 
      key={uniqueKey} 
      selectedToken={selectedToken} 
      isWidget={true} 
    />,
    size: { width: 1100, height: 500 },
    contentStyle: { 
      padding: 0,
      overflow: 'hidden' 
    },
    glassMorphism: true,
    containerStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      border: '1px solid rgba(36, 195, 91, 0.2)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)',
      overflow: 'hidden'
    }
  };
};

export default AdvancedCharts;